/**
 * voice-v8 — Tool-Card-Renderer (Tag 17), der eine Einstieg `buildToolCard`.
 *
 * Pro Tool-Name eine spezialisierte Card. Alles via createElement,
 * keine User-Inhalte gehen durch innerHTML.
 *
 * 15.10.2026 (Plan „Karten zu 100 % einheitlich“, Welle 3): die Bauer wohnen je
 * Familie in lib/karten/<familie>.ts (je unter 600 Zeilen); hier steht nur die
 * Verteilung. Wer keine eigene Karte hat, bekommt die Anzeige des Servers
 * (karten/hilfe.ts → ohneEigeneKarte). Die öffentliche Schnittstelle dieser
 * Datei ist unverändert — die Re-Exporte unten halten sie.
 */

import { ABSPIEL_WERKZEUG, abspielZiel, merkeKartenStart } from "./abspielen.js";
import type { ToolCardMessage } from "./webrtc.js";
import { baueAlltagKarte } from "./alltag-karte.js";
import { baueKarte, hinweisKarte, kartenTextSaeubern, kartenTitel } from "./karte.js";
import { baueDateiVorleseKarte, istVorleseUrl } from "./datei-vorlese-karte.js";
import { baueLiedAbspielKarte, baueLiedKarte, meldeLiedStand } from "./lied-karte.js";
import { leseRechnungsKarte } from "./rechnung-karte.js";
import { wartetAufJa } from "./karten-text.js";
import { buildAnkerCard, buildAnkerListeCard } from "./karten/anker.js";
import { buildClaudeTaskCard, buildTaskDelegateCard, buildTaskListCard, buildTicketCard } from "./karten/aufgaben.js";
import { buildImageCard, buildImageSearchCard } from "./karten/bilder.js";
import { buildBlenderCard, buildModellCard } from "./karten/blender.js";
import { buildBrowserCard, buildOpenAppCard, buildOpenViewCard } from "./karten/browser.js";
import { buildDriveSearchCard, buildFileDownloadCard, buildMakePdfCard, buildOpenUrlCard, buildReadFileCard, buildTaskFilesCard } from "./karten/datei.js";
import { buildReminderDoneCard, buildTimerCard } from "./karten/erinnerung.js";
import { buildDeferredCard, buildErrorCard } from "./karten/fehler.js";
import { buildChatHistorySearchCard, buildMemoryDeleteCard, buildMemorySaveCard, buildMemorySearchCard, buildStammdatenCard } from "./karten/gedaechtnis.js";
import { freigabeStand, ohneEigeneKarte, toolLabel, unwrapResult } from "./karten/hilfe.js";
import { buildCalendarCreateCard, buildCalendarDeleteCard, buildCalendarListCard } from "./karten/kalender.js";
import { buildHumanDesignCard, buildVocabCard, buildWhoopCard } from "./karten/koerper.js";
import { buildTrainingCard } from "./karten/training.js";
import { buildKnopfRueckfrageCard } from "./karten/knopf-rueckfrage.js";
import { buildCallCard, buildContactCard, buildVerzeichnisCard } from "./karten/kontakte.js";
import { buildAblaufCard, buildMacAppCard, buildScreenCard } from "./karten/mac.js";
import { buildGmailDraftCard, buildGmailDraftSendCard, buildGmailDraftsListCard, buildGmailReadCard, buildGmailSearchCard, buildGmailSendCard, buildMailCard } from "./karten/mail.js";
import { buildFokusVideosCard, buildMeditationCard, buildVideoAnalysisCard } from "./karten/medien.js";
import { buildNarrateCard, buildProjectActionCard, buildProjectFileActionCard } from "./karten/projekte.js";
import { buildReceiptAnalyzeCard, buildReceiptBookedCard, buildRechnungCard } from "./karten/rechnung.js";
import { buildFlightSearchCard, buildLiveTranslateCard, buildUnterkunftCard } from "./karten/reise.js";
import { buildConfirmCard, buildNutzerLageCard, buildScheduleTaskCard, buildShellExecCard, buildTextBlockCard } from "./karten/system.js";
import { buildLocationCard, buildOebbCard, buildRouteCard, buildWeatherCard } from "./karten/unterwegs.js";
import { buildAmazonSearchCard, buildDeepResearchCard, buildGegenprobeCard, buildLinkVorschau, buildSearchCard } from "./karten/web.js";

// Die Uebersetzung wohnt seit 21.09. in fehler-worte.ts; wer sie hier holt, bekommt sie weiter.
export { menschlicherFehler } from "./fehler-worte.js";
// Die Liste, ihre Einträge und „und N weitere" wohnen in lib/karte.ts; wer
// sie hier holt (Tests, rueckfrage-oben), bekommt sie weiter.
export { LISTE_MAX, kuerzeListe, weitereHinweis } from "./karte.js";
export { buildRechnungCard } from "./karten/rechnung.js";
export { whoopRing } from "./karten/koerper.js";
export { leseOebbErgebnis, oebbHatZuege, waehleFinaleOebbKarte, routeKartenText } from "./karten/unterwegs.js";
export { bestaetigungsFrage, type ConfirmStand, confirmKarteVon, setzeConfirmStand, beantworteConfirmKarte } from "./karten/system.js";


/**
 * Die eine Tür für jede Werkzeugkarte. (15.10.2026)
 *
 * Martin: *„mit diesen komischen Pfaden, Buttons und allem“* — am Ende geht
 * jede Karte durch die Endreinigung des Grundgerüsts (lib/karte.ts,
 * kartenTextSaeubern): kein roher Pfad, kein Satz an das Modell im
 * sichtbaren Text, egal welche Karte ihn gesetzt hat.
 */
export function buildToolCard(msg: ToolCardMessage): HTMLElement {
  return kartenTextSaeubern(baueWerkzeugKarte(msg));
}

function baueWerkzeugKarte(msg: ToolCardMessage): HTMLElement {
  // 07.10.2026: die Liedkarte erfährt, was aus ihrem Ja wurde (Zusage oder Absage des Servers).
  if (msg.tool === "generate_music") meldeLiedStand(msg.result, msg.isError);
  /*
   * 15.10.2026: ein Halt des Ja-Tors ist für JEDES Werkzeug dasselbe —
   * nichts getan, Martins Ja steht aus. EINE Karte dafür, vor jeder
   * Verzweigung; vorher zeigte jede Kartenfamilie den Hinweis an das Modell
   * („NICHTS in Blender ausgeführt. Frag den Nutzer …“) als ihren Fehler.
   */
  // 21./22.10.2026: Rückfragen mit Ja-Knopf (Trainingsplan, Shorts) haben ihre eigene Karte — dieselbe Prüfung wie die Schritte-Weiche
  // in app.ts (karten/knopf-rueckfrage.ts), damit Weiche und Kartenbau nie auseinanderlaufen.
  const knopfKarte = buildKnopfRueckfrageCard(msg.tool, msg.result);
  if (knopfKarte) return knopfKarte;
  // 25.10.2026: der Mail-Vorschlag von send_mail (tools/send_mail.ts) zeigt Empfänger, Betreff und Text — Martin soll sehen, worauf er Ja sagt.
  if (msg.tool === "send_mail" && !msg.isError && wartetAufJa(unwrapResult(msg.result))) return buildMailCard(msg.result);
  if (msg.tool !== "generate_music" && wartetAufJa(unwrapResult(msg.result))) {
    const warten = baueKarte(msg.tool.replace(/_/g, "-"));
    warten.appendChild(kartenTitel(toolLabel(msg.tool)));
    return freigabeStand(warten);
  }
  if (msg.isError) {
    // 14.10.2026: die Routenkarte nennt ihren Grund selbst — auch bei ok:false.
    if (msg.tool === "route_directions") return buildRouteCard(msg.result);
    return buildErrorCard(msg);
  }
  // Pre.851 (#136): Deferred-Ergebnis (Tool läuft noch im Hintergrund) →
  // eigene Status-Karte statt generischem "deferred: true"-Key-Value-Dump.
  const dr = unwrapResult(msg.result);
  if (dr.deferred === true) {
    return buildDeferredCard(msg);
  }
  switch (msg.tool) {
    case "stammdaten_merken":
    case "stammdaten_vergessen":
      return buildStammdatenCard(msg.tool, msg.result);
    case "unterkunft_zeigen":
      return buildUnterkunftCard(msg.result);
    case "whoop_vitals":
      return buildWhoopCard(msg.result);
    case "training_plan_zeigen":
      return buildTrainingCard(msg.result);
    case "vocab_add":
    case "vocab_due":
    case "vocab_result":
    case "vocab_stats":
      return buildVocabCard(msg.tool, msg.result);
    case "gegenprobe":
      return buildGegenprobeCard(msg.result);
    case "weather":
      return buildWeatherCard(msg.result);
    case "user_location_get":
      return buildLocationCard(msg.result);
    case "oebb_journey":
      return buildOebbCard(msg.result);
    case "route_directions":
      return buildRouteCard(msg.result);
    case "ticket_create":
      return buildTicketCard(msg.result);
    case "anker_zeigen":
    case "anker_anlegen":
      return buildAnkerCard(msg.tool, msg.result);
    case "anker_liste":
      return buildAnkerListeCard(msg.result);
    case "human_design":
      return buildHumanDesignCard(msg.result);
    case "task_files":
      return buildTaskFilesCard(msg.result);
    case "attach_file":
      return buildFileDownloadCard(msg.result);
    case "ask_confirm":
      return buildConfirmCard(msg.result);
    case "schedule_task":
      return buildScheduleTaskCard(msg.result);
    case "deliver_text":
      return buildTextBlockCard(msg.result);
    case "timer_local":
      return buildTimerCard(msg.result);
    case "send_mail":
      return buildMailCard(msg.result);
    case "call":
      return buildCallCard(msg.result);
    case "web_search":
      return buildSearchCard(msg.result);
    case "deep_research":
      return buildDeepResearchCard(msg.result);
    case "reminder_done":
      return buildReminderDoneCard(msg.result);
    case "fokus_videos":
      return buildFokusVideosCard(msg.result);
    case "analyze_video":
      return buildVideoAnalysisCard(msg.result);
    case "web_image_search":
      return buildImageSearchCard(msg.result);
    // 15.10.2026: Wortlaut, Aufnahme, Suche — eine Zeile je Aufnahme, nie der Text (lib/alltag-karte.ts).
    case "alltag_search":
    case "alltag_detail":
    case "alltag_lesen":
      return baueAlltagKarte(msg.tool, msg.result);
    case "memory_search":
      return buildMemorySearchCard(msg.result);
    case "memory_save":
      return buildMemorySaveCard(msg.result);
    case "memory_delete":
      return buildMemoryDeleteCard(msg.result);
    case "project_file_create":
    case "project_file_update":
    case "project_file_delete":
    case "project_file_get":
      return buildProjectFileActionCard(msg.tool, msg.result);
    case "narrate_to_mp3":
      return buildNarrateCard(msg.result);
    case "drive_search":
      return buildDriveSearchCard(msg.result);
    case "gmail_search":
      return buildGmailSearchCard(msg.result);
    case "gmail_read_message":
      return buildGmailReadCard(msg);
    case "nutzer_lage":
      return buildNutzerLageCard(msg.result);
    case "claude_task_server":
      return buildClaudeTaskCard(msg.result);
    case "expense_analyze_upload":
      return buildReceiptAnalyzeCard(msg.result);
    case "receipt_booked":
      return buildReceiptBookedCard(msg.result);
    case "amazon_search":
      return buildAmazonSearchCard(msg.result);
    case "flight_search":
      return buildFlightSearchCard(msg.result);
    case "live_translate":
      return buildLiveTranslateCard(msg.result);
    case "gmail_send":
      return buildGmailSendCard(msg.result);
    case "gmail_draft_create":
      return buildGmailDraftCard(msg.result);
    case "gmail_draft_send":
      return buildGmailDraftSendCard(msg.result);
    case "gmail_drafts_list":
      return buildGmailDraftsListCard(msg.result);
    case "calendar_list":
      return buildCalendarListCard(msg.result);
    case "calendar_delete":
      return buildCalendarDeleteCard(msg.result);
    case "calendar_create":
      return buildCalendarCreateCard(msg.result);
    case "make_pdf":
      return buildMakePdfCard(msg.result);
    // 2026-07-14 (Martin, Musik-Modul); seit 07.10.2026 in lied-karte.ts — Vorschlag
    // mit Text im Wortlaut und EINEM Ja, Probestück, Lied mit Prüfung.
    case "generate_music":
      return baueLiedKarte(msg.result);
    case "morgenmeditation":
      return buildMeditationCard(msg.result);
    // 27.09.2026 (Ticket 2698): die Meditation mit ihrer Karte. Ein
    // gelungener Start von Spiegel oder Alltag kommt nie hierher
    // (abspielenOhneKarte) — was bleibt, ist ein Fehlschlag mit seinem Satz.
    case ABSPIEL_WERKZEUG: {
      if (abspielZiel(msg.result) === "meditation") {
        return buildMeditationCard(msg.result, (a) => merkeKartenStart(msg.callId, a));
      }
      // 07.10.2026: „Spiel das Lied noch einmal“ — der Player des Stücks, Start nach Vesperas Satz.
      // Nur ein gefundenes Lied hat einen Player; „Mehrere passen“ oder „keins“ ist die Zeile darunter.
      if (abspielZiel(msg.result) === "lied" && unwrapResult(msg.result).ok === true) {
        return baueLiedAbspielKarte(msg.result, (a) => merkeKartenStart(msg.callId, a));
      }
      // 19.10.2026: eine Textdatei wörtlich — der Player liest, nicht die Antwort.
      if (abspielZiel(msg.result) === "datei" && unwrapResult(msg.result).ok === true && istVorleseUrl(String(unwrapResult(msg.result).url ?? ""))) {
        return baueDateiVorleseKarte(msg.result, (a) => merkeKartenStart(msg.callId, a));
      }
      const fehler = unwrapResult(msg.result).error;
      return hinweisKarte(typeof fehler === "string" && fehler ? fehler : "Ließ sich nicht abspielen.");
    }
    case "generate_image":
    case "edit_image":
      return buildImageCard(msg.tool, msg.result);
    case "read_file":
      return buildReadFileCard(msg.result);
    case "open_app":
      return buildOpenAppCard(msg.result);
    case "open_url":
      return buildOpenUrlCard(msg.result);
    case "browser_open_url":
    case "browser_tab_context":
    case "browser_get_active_tab":
    case "browser_get_page_text":
    case "browser_screenshot":
    case "browser_click":
    case "browser_type":
    case "browser_form_overview":
    case "browser_scroll":
    case "browser_list_tabs":
    case "browser_switch_tab":
    case "browser_close_tab":
    case "browser_form_submit":
      return buildBrowserCard(msg.tool, msg.result);
    case "contact_create":
    case "contact_update":
    case "contact_delete":
    case "contact_find":
    case "contact_list":
    case "contact_call":
      return buildContactCard(msg.tool, msg.result);
    case "research_directory":
      return buildVerzeichnisCard(msg.result);
    case "open_view":
      return buildOpenViewCard(msg.result);
    case "project_create":
    case "project_set_active":
    case "project_link":
    case "project_unlink":
    case "project_delete":
    case "project_update":
      return buildProjectActionCard(msg.tool, msg.result);
    case "task_delegate":
      return buildTaskDelegateCard(msg.result);
    case "task_list":
      return buildTaskListCard(msg.result);
    case "chat_history_search":
      return buildChatHistorySearchCard(msg.result);
    case "shell_exec":
      return buildShellExecCard(msg.result);
    case "screen_context":
    case "screen_take_screenshot":
    case "screen_look":
    case "screen_mouse_click":
    case "screen_mouse_move":
    case "screen_double_click":
    case "screen_right_click":
    case "screen_type_text":
    case "screen_hotkey":
    case "screen_scroll":
    case "screen_list_apps":
    case "claude_task":
      return buildScreenCard(msg.tool, msg.result);
    case "blender_szene":
    case "blender_python":
    case "blender_bild":
    case "blender_nahaufnahme":
    case "blender_render":
    case "blender_speichern":
    case "blender_oeffnen":
    case "blender_pruefen":
    case "blender_aufsetzen":
    case "blender_objekt_stellen":
    case "blender_herde_stellen":
    case "mac_anbindungen":
      return buildBlenderCard(msg.tool, msg.result);
    // 15.10.2026: Modelle suchen und holen — Treffer als Liste, der Import als
    // Zeilen; vorher fiel beides in die generische Karte.
    case "modell_suchen":
    case "modell_importieren":
      return buildModellCard(msg.tool, msg.result);
    case "browser_ablauf":
    case "screen_ablauf":
      return buildAblaufCard(msg.tool, msg.result);
    case "mac_numbers_table":
    case "mac_pages_doc":
    case "mac_keynote_present":
    case "mac_notes_create":
    case "mac_notes_append":
    case "mac_notes_search":
    case "mac_reminders_create":
    case "mac_reminders_list":
    case "mac_mail_compose":
    case "mac_music":
    case "mac_safari_open":
    case "mac_finder_reveal":
    case "mac_spotlight_search":
    case "mac_clipboard":
    case "mac_system_volume":
    case "mac_applescript":
      return buildMacAppCard(msg.tool, msg.result);
    case "read_url":
      return buildLinkVorschau(msg);
    // 05.10.2026 (Ticket 2996): die Rechnung als Karte mit Knopf. Ohne die
    // Zeile „Karte: …“ des Servers (STOPP, Fehler, ältere Züge) bleibt der Text.
    case "faktura_invoice_create":
    case "faktura_invoice_clone":
    case "faktura_invoice_get":
    case "faktura_invoice_update":
    case "faktura_invoice_item_add": {
      const rechnung = leseRechnungsKarte(msg.tool, msg.result);
      return rechnung ? buildRechnungCard(rechnung) : ohneEigeneKarte(msg);
    }
    default:
      return ohneEigeneKarte(msg);
  }
}
